/* eslint-disable @tanstack/query/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useInfiniteScroll from 'hooks/useInfiniteScroll/useInfiniteScroll';
import {
  TNotification,
  TNotificationSubscription,
} from 'lendingApi/notifications';
import { TNotificationsData } from 'types';
import {
  getNotificationSubscriptions,
  getNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  updateNotificationSubscriptions,
} from './useNotifications.api';

export const useNotifications = (
  containerRef: React.RefObject<HTMLDivElement>,
  introducerId: string,
): TNotificationsData => {
  const [isShowingMore, setIsShowingMore] = useState(false);
  const usingInfiniteScroll = isShowingMore;
  const queryClient = useQueryClient();

  /**
   * Fetch (and fetch more) notifications
   */
  const {
    data: notifications,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isLoadingMore,
    totalUnseen,
    isError,
    error,
    isEmpty,
  } = useInfiniteScroll<TNotification>({
    queryKey: ['notifications'],
    apiFunction: (page) => getNotifications(introducerId, page),
    containerRef,
    preventFetch: !usingInfiniteScroll || !introducerId,
    refetchInterval: 2 * 1000 * 60,
  });

  /**
   * Get subscriptions
   */
  const { isLoading: isFetchingSubscriptions, data: subscriptions } = useQuery({
    queryKey: ['notificationSubscriptions'],
    queryFn: () => getNotificationSubscriptions(introducerId),
  });

  /**
   * Update subscriptions
   */
  const updateSubscriptionsMutation = useMutation({
    mutationFn: (newSubscriptions: TNotificationSubscription[]) =>
      updateNotificationSubscriptions(introducerId, newSubscriptions),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notificationSubscriptions'],
      });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  /**
   * Mark item(s) as read
   */
  const markItemAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => {
      // Mark all
      if (notificationId === 'all') {
        return markAllNotificationsAsRead(introducerId);
      }

      // Mark selected
      const { application_id } = notifications?.find(
        (item) => item.notification_id === notificationId,
      ) as TNotification;

      return markNotificationAsRead(
        introducerId,
        application_id,
        notificationId,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const handleShowMore = () => {
    if (isShowingMore) return;
    setIsShowingMore(true);
    fetchNextPage();
  };

  const markItemAsRead = (notificationId: string) => {
    const notification = notifications?.find(
      (item) => item.notification_id === notificationId,
    );
    if (!notification || notification.read_time) return;
    markItemAsReadMutation.mutate(notificationId);
  };

  useEffect(() => {
    if (notifications && notifications.length > 5 && !isShowingMore) {
      setIsShowingMore(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  return {
    error,
    isLoading: isLoading || isLoadingMore,
    isError,
    isUpdatingSubscriptions: updateSubscriptionsMutation.isPending,
    updateSubscriptions: (subscriptions: TNotificationSubscription[]) =>
      updateSubscriptionsMutation.mutate(subscriptions),
    isFetchingSubscriptions,
    notifications: notifications || [],
    isShowingMore: usingInfiniteScroll,
    handleShowMore,
    hasMoreNotifications: hasNextPage,
    numberUnread: totalUnseen || 0,
    markItemAsRead,
    markAllAsRead: () => markItemAsReadMutation.mutate('all'),
    isMarkingAsRead: markItemAsReadMutation.isPending,
    hasData: !isEmpty && !isError && notifications !== null,
    subscriptions,
    isEmpty,
  };
};
